import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { enrichMealNutrients } from '@/hooks/useMeals';

export type Partner = {
  id: string;
  name: string | null;
  avatar_url: string | null;
  auto_accept: boolean;
};

export type IncomingShare = {
  id: string;
  from_user_id: string;
  payload: { items: any[] };
  created_at: string;
};

/**
 * «Едим вместе»: партнёры и присланные ими приёмы пищи.
 *
 * Связь заводится ОДИН раз по личному коду — тому же, что для приглашений.
 * Дальше отправка и приём в одно касание, а при включённом автоприёме — вообще
 * без касаний: то, что записал партнёр, само появляется в дневнике.
 *
 * Связь на уровне аккаунта, не платформы: муж в Telegram и жена в вебе связываются
 * так же, как двое из Telegram.
 */
export function usePartners() {
  const { profile } = useUserStore();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [incoming, setIncoming] = useState<IncomingShare[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    const [p, inbox] = await Promise.all([
      supabase.rpc('my_partners'),
      supabase
        .from('meal_shares')
        .select('id, from_user_id, payload, created_at')
        .eq('to_user_id', profile.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(5),
    ]);
    setPartners((p.data as Partner[]) ?? []);
    setIncoming((inbox.data as IncomingShare[]) ?? []);
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Возврат в приложение — перечитываем: партнёр мог прислать, пока нас не было.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refetch]);

  // Тихая проверка раз в 45 секунд, пока приложение открыто.
  //
  // Без неё присланное появляется только при следующем запуске: Telegram держит
  // мини-приложение в памяти и событие смены видимости шлёт не всегда. Партнёр
  // отправил, пока ты сидишь в приложении, — и ты об этом не узнаёшь.
  // Запрос лёгкий (до пяти строк своих же входящих), фоном его не гоняем.
  useEffect(() => {
    if (!profile?.id) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refetch();
    }, 45000);
    return () => clearInterval(t);
  }, [profile?.id, refetch]);

  const addByCode = useCallback(
    async (code: string): Promise<{ ok: boolean; error?: string; name?: string }> => {
      const { data, error } = await supabase.rpc('add_partner', { p_code: code });
      if (error) return { ok: false, error: 'server' };
      if (data?.ok) await refetch();
      return data ?? { ok: false, error: 'server' };
    },
    [refetch],
  );

  const remove = useCallback(
    async (partnerId: string) => {
      await supabase.rpc('remove_partner', { p_partner_id: partnerId });
      await refetch();
    },
    [refetch],
  );

  const setAutoAccept = useCallback(
    async (partnerId: string, value: boolean) => {
      setPartners((list) => list.map((p) => (p.id === partnerId ? { ...p, auto_accept: value } : p)));
      const { error } = await supabase
        .from('partners')
        .update({ auto_accept: value })
        .eq('user_id', profile!.id)
        .eq('partner_id', partnerId);
      if (error) await refetch(); // не записалось — возвращаем как было
    },
    [profile, refetch],
  );

  const accept = useCallback(
    async (shareId: string) => {
      setIncoming((list) => list.filter((s) => s.id !== shareId)); // убираем сразу
      const { data, error } = await supabase.rpc('accept_meal_share', { p_id: shareId });
      if (error) {
        await refetch();
        return;
      }
      // Досчёт микронутриентов. При обычном сохранении его запускает AddMeal,
      // а здесь приём создаётся на сервере — без этого блюдо осталось бы
      // без витаминов и воды до ночного прохода.
      if (data?.meal_id) enrichMealNutrients(data.meal_id).catch(() => {});
    },
    [refetch],
  );

  const decline = useCallback(
    async (shareId: string) => {
      setIncoming((list) => list.filter((s) => s.id !== shareId));
      await supabase.from('meal_shares').update({ status: 'declined' }).eq('id', shareId);
    },
    [],
  );

  return { partners, incoming, loading, addByCode, remove, setAutoAccept, accept, decline, refetch };
}
